import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { map, shareReplay,  filter } from 'rxjs/operators';
import { union } from 'underscore';
import { CookieService } from 'ngx-cookie-service';

import { JournodexApiService } from '../../../api/journodex/journodex.api.service';
import { JournodexGeographyEntityApiModel } from '../../../api/journodex/model/journodex-geography-entity-api.model';
import { JournodexGeographyModel } from '../models/journodex-geography.model';
import { GeographyQueryModel } from '../../../api/mentions/model/geography-query.model';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';

import { CoreCacheService } from '../../../Core/Cache/services/core-cache.service';
import { JournodexGetChildRegionModel } from '../../../api/journodex/model/journodex-get-child-region.model';
import { JournodexGetImmediateChildRegionModel } from '../../../api/journodex/model/journodex-get-immediate-child-region.model';
import { CountryApiModel } from '../../../api/journodex/model/country-api.model';
import { LanguageTagEnum } from '../../../Core/Localization/enums/language-tag.enum';

import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

interface RegionChildrenCache {
    count: number;
    type?: GeographyTypesEnum;
    ids?: number[];
}

@Injectable()
export class JournodexRegionsService implements OnDestroy {

    private readonly coreCacheId: string = 'JOURNODEX-REGION';
    private readonly cacheTtl: number = 60 * 60 * 12; // 12 hours to expire
    private readonly cacheCheckInterval: number = 60 * 10; // 10 minutes to recheck expiries
    private readonly cacheGeoIDKey: string = 'geoID-';

    private allCountries: CountryApiModel[];
    private allCountries$: Observable<CountryApiModel[]>;

    protected locale: string = LanguageTagEnum.en_US;
    protected subscriptions: Subscription[] = [];

    constructor(
        protected journodexApiService: JournodexApiService,
        protected coreCacheService: CoreCacheService,
        protected cookieService: CookieService,
        protected currentUserStateService: CurrentUserStateService,
        protected localizationService: LocalizationService
    ) {
        this.coreCacheService.create(
            this.coreCacheId,
            this.cacheTtl,
            this.cacheCheckInterval
        );
        this.allCountries$ = this.journodexApiService.getAllCountries().pipe(shareReplay(1));

        this.allCountries$.subscribe((countries) => {
            this.allCountries = countries;
        });

        this.subscriptions.push(
            this.currentUserStateService.getDataReferenceLanguageTag().pipe(filter(x => !!x)).subscribe(languageTag => {
                this.locale = languageTag;
            })
        );
    }


    /**
     * Create an observable of data matching a search query for geographic locations
     */
    searchGeographicLocations(query: string, locale?: string): Observable<JournodexGeographyModel[]> {
        locale = locale || this.locale;
        return this.journodexApiService.performQuickSearchGeography(query, locale, false).pipe(map(
            quickSearchGeographicResponse => {
                // save locations to cache
                return quickSearchGeographicResponse.map(apiEntity => new JournodexGeographyModel(apiEntity, locale, this.localizationService));
            }
        ));
    }

    /**
     * Map to the more complex (contains more geographic details) model from Journodex data
     *
     * @param simpleRegionInfo List of specific geographic types id values
     */
    mapToComplexRegionInfo(
        simpleRegionInfo: GeographyQueryModel
    ): Observable<JournodexGeographyModel[]> {
        const journodexCalls: Observable<JournodexGeographyModel[]>[] = [];

        if (simpleRegionInfo.continents && simpleRegionInfo.continents.length > 0) {
            const continentsRequest = this.getRegions(
                simpleRegionInfo.continents,
                GeographyTypesEnum.continent
            );
            if (continentsRequest !== null) {
                journodexCalls.push(continentsRequest);
            }
        }

        if (simpleRegionInfo.countries && simpleRegionInfo.countries.length > 0) {
            const countriesRequest = this.getRegions(
                simpleRegionInfo.countries,
                GeographyTypesEnum.country
            );
            if (countriesRequest !== null) {
                journodexCalls.push(countriesRequest);
            }
        }

        if (simpleRegionInfo.provinces && simpleRegionInfo.provinces.length > 0) {
            const provincesRequest = this.getRegions(
                simpleRegionInfo.provinces,
                GeographyTypesEnum.province
            );
            if (provincesRequest !== null) {
                journodexCalls.push(provincesRequest);
            }
        }

        if (simpleRegionInfo.counties && simpleRegionInfo.counties.length > 0) {
            const countiesRequest = this.getRegions(
                simpleRegionInfo.counties,
                GeographyTypesEnum.county
            );
            if (countiesRequest !== null) {
                journodexCalls.push(countiesRequest);
            }
        }

        if (simpleRegionInfo.cities && simpleRegionInfo.cities.length > 0) {
            const citiesRequest = this.getRegions(
                simpleRegionInfo.cities,
                GeographyTypesEnum.city
            );
            if (citiesRequest !== null) {
                journodexCalls.push(citiesRequest);
            }
        }

        if (journodexCalls.length === 0) {
            return of([]);
        } else if (journodexCalls.length === 1) {
            return journodexCalls[0];
        }

        return forkJoin(journodexCalls).pipe(
            map((responses) => {
                const flatResponse = [];

                responses.forEach((response) => flatResponse.push(...response));

                return flatResponse;
            })
        );
    }

    /**
     * Merge data from GeographyQueryModels returning a new instance of the union of the provided models
     */
    mergeGeographyQueryModels(
        ...geoModels: GeographyQueryModel[]
    ): GeographyQueryModel {
        const modelProperties: string[] = [
            'continents',
            'countries',
            'provinces',
            'counties',
            'cities'
        ];

        // Clone the first object
        const mergedData: GeographyQueryModel = {
            cities: [],
            continents: [],
            counties: [],
            countries: [],
            provinces: [],
        };

        geoModels.forEach(geoModel => {
            if (!geoModel) {
                return;
            }
            for (const property of modelProperties) {
                if (!mergedData.hasOwnProperty(property)) {
                    mergedData[property] = geoModel.hasOwnProperty(property) ? geoModel[property] : [];
                } else {
                    mergedData[property] = geoModel.hasOwnProperty(property)
                        ? union(mergedData[property], geoModel[property])
                        : mergedData[property];
                }
            }
        });

        return mergedData;
    }

    /**
     * Adds a region to the cache
     */
    private addRegionToCache(region: JournodexGeographyEntityApiModel): void {
        if (!region) {
            return;
        }
        const regionType = region.geographyType;
        const regionId = region[regionType.toLowerCase()].id;

        // Sometimes we don't have localizations for a specific locale, in those cases, we should add it to the cache as empty
        // so we know not to request it again
        if (!this.hasCorrectLocalization(region)) {
            region[region.geographyType.toLowerCase()].names.push({
                value: '',
                locale: this.locale
            });
        }

        // TODO : Could perhaps be improved by not caching every geo twice
        // but it is deemed that memory is plentiful.

        // Add with geo entity id as key
        this.coreCacheService.add(
            this.coreCacheId,
            `${regionType}-${regionId}`,
            region
        );

        // Add with geo id as key
        this.coreCacheService.add(
            this.coreCacheId,
            `${this.cacheGeoIDKey}${region.id}`,
            region
        );
    }

    /**
     * Adds the list of children for a region to the cache
     */
    private addChildListToCache(parentRegion: JournodexGeographyModel, childRegions: JournodexGeographyEntityApiModel[]): void {
        const parentType = parentRegion.geographicType;
        const parentId = parentRegion[parentType.toLowerCase()].id;

        if (!childRegions || childRegions.length < 1) {
            this.coreCacheService.add(
                this.coreCacheId,
                `${parentType}-${parentId}-children`,
                {
                    count: 0
                } as RegionChildrenCache
            );
        } else {
            const childrenType = childRegions[0].geographyType;
            const childrenTypeProperty = childrenType.toLowerCase();
            const childrenIds = childRegions.map(child => child[childrenTypeProperty].id);
            this.coreCacheService.add(
                this.coreCacheId,
                `${parentType}-${parentId}-children`,
                {
                    type: childrenType,
                    count: childrenIds.length,
                    ids: childrenIds
                } as RegionChildrenCache
            );
        }
    }

    /**
     * Gets a set of regions by id and type
     */
    getRegions(regionIds: number[], entityType: GeographyTypesEnum): Observable<JournodexGeographyModel[]> {
        const cachedRegions: JournodexGeographyModel[] = [];
        const uncachedRegionIds: number[] = [];

        // get the regions that are already cached
        regionIds.forEach((id) => {
            const cacheValue = this.coreCacheService.get(this.coreCacheId, `${entityType}-${id}`);
            if (cacheValue && this.hasCorrectLocalization(cacheValue)) {
                cachedRegions.push(new JournodexGeographyModel(cacheValue, this.locale, this.localizationService));
            } else {
                uncachedRegionIds.push(id);
            }
        });

        if (uncachedRegionIds.length < 1) {
            return of(cachedRegions);
        }

        // get the remaining regions from the backend
        const apiRequest = this.journodexApiService.performLookupByGeoEntities(uncachedRegionIds, entityType, this.locale);

        // cache the regions from the backend
        apiRequest.subscribe(
            (entities) => {
                entities.forEach((entity) => {
                    this.addRegionToCache(entity.geographyDetail);
                });
            }
        );

        // assemble the full list of regions and return
        return apiRequest.pipe(map((entities) => {
            const regions = [];

            regionIds.forEach((id) => {
                const cachedRegion = cachedRegions.find((region) => +region.id === +id);

                if (cachedRegion) {
                    regions.push(cachedRegion);
                } else {
                    const entity = entities.find((region) => +region.geoEntityId === +id);

                    if (entity) {
                        regions.push(new JournodexGeographyModel(entity.geographyDetail, this.locale, this.localizationService));
                    }
                }
            });

            return regions;
        }));
    }

    /**
     * Gets the list of continents
     */
    getContinents(): Observable<JournodexGeographyModel[]> {
        const getContinentParam = new JournodexGetChildRegionModel(
            GeographyTypesEnum.continent
        );

        const continentsCache = this.coreCacheService.get(
            this.coreCacheId,
            GeographyTypesEnum.continent
        );

        if (continentsCache && continentsCache.length > 0) {
            return this.getRegions(continentsCache, GeographyTypesEnum.continent);
        }

        return this.journodexApiService.getChildRegions(getContinentParam).pipe(
            map((entities) => {
                const regions = entities.map((entity) => {
                    this.addRegionToCache(entity);

                    return new JournodexGeographyModel(entity, this.locale);
                });

                this.coreCacheService.add(
                    this.coreCacheId,
                    GeographyTypesEnum.continent,
                    regions.map(region => region.id)
                );

                return regions;
            }));
    }


    /**
     * Gets the direct child regions of a parent region.
     */
    getChildRegions(parentRegion: JournodexGeographyModel): Observable<JournodexGeographyModel[]> {
        const requestParams = new JournodexGetImmediateChildRegionModel();
        requestParams.parentType = parentRegion.geographicType;
        requestParams.parentId = parentRegion.id;
        requestParams.locale = this.locale;

        const regionChildrenCache: RegionChildrenCache = this.coreCacheService.get(
            this.coreCacheId,
            `${parentRegion.geographicType}-${parentRegion.id}-children`
        );

        if (regionChildrenCache) {
            if (regionChildrenCache.count === 0) {
                return of([]);
            }

            return this.getRegions(regionChildrenCache.ids, regionChildrenCache.type);
        }

        return this.journodexApiService.getImmediateChildRegions(requestParams).pipe(
            map((entities) => {
                const regions = entities.map((entity) => {
                    this.addRegionToCache(entity);

                    return new JournodexGeographyModel(entity, this.locale);
                });
                this.addChildListToCache(parentRegion, entities);

                return regions;
            }));
    }

    getCountriesByIsoCodes(ids: string[]): Observable<CountryApiModel[]> {
        return this.allCountries$.pipe(
            map(countries => countries.filter(country => ids.includes(country.countryCode)))
        );
    }

    getCountryByIsoCode(isoCode: string): CountryApiModel {
        return this.allCountries?.find(country => country.countryCode === isoCode);
    }

    getCountryById(id: string): CountryApiModel {
        return this.allCountries?.find(country => country.id === id);
    }

    getByGeographyIds = (ids: number[]) => {
        const uncachedRegionIds: number[] = [];
        const cachedRegions: JournodexGeographyModel[] = [];

        ids.forEach((id) => {
            const cacheValue = this.coreCacheService.get(this.coreCacheId, `${this.cacheGeoIDKey}${id}`);
            if (cacheValue && this.hasCorrectLocalization(cacheValue)) {
                cachedRegions.push(new JournodexGeographyModel(cacheValue, this.locale, this.localizationService));
            } else {
                uncachedRegionIds.push(id);
            }
        });

        if (uncachedRegionIds.length > 0) {
            return this.journodexApiService.batchGetGeos(uncachedRegionIds).pipe(
                map(geosFromApi => {
                    const regionsInOrder: JournodexGeographyModel[] = [];

                    ids.forEach(id => {
                        const geo = cachedRegions.find(x => x.collectionId === id);
                        if (geo) {
                            regionsInOrder.push(geo);
                        } else {
                            const apiGeo = geosFromApi.find(x => x.id === id);
                            if (apiGeo) {
                                regionsInOrder.push(new JournodexGeographyModel(apiGeo, this.locale, this.localizationService));
                                this.addRegionToCache(apiGeo);
                            } else {
                                console.error(`Unknown Country Geo ID: ${id}`);
                            }
                        }
                    });
                    return regionsInOrder;
                })
            );
        } else {
            // These are already in order
            return of(cachedRegions);
        }
    };

    /**
     * Verifies that the cached entity has a name value for the current this.locale value
     */
    private hasCorrectLocalization(cacheEntity: any): boolean {
        if (!cacheEntity || !cacheEntity.geographyType) {
            return false;
        }

        const localeNameModels: any[] = cacheEntity[cacheEntity.geographyType.toLowerCase()].names;

        for (const localeNameModel of localeNameModels) {
            if (localeNameModel.locale === this.locale && localeNameModel.value) {
                return true;
            }
        }

        return false;
    }

    private autoCompleteCountry(searchString: string): Observable<CountryApiModel[]> {
        return this.allCountries$.pipe(
            map(countries => {
                return countries.filter(x => x.name.toLowerCase().indexOf(searchString.toLowerCase()) >= 0);
            })
        );
    };

    autoCompleteCountryWithCode(searchString: string): Observable<CountryApiModel[]> {
        return this.autoCompleteCountry(searchString).pipe(
            map(x => x.filter(country => !!country.countryCode))
        );
    };

    autoCompleteCountryLabelOnly(searchString: string): Observable<string[]> {
        return this.autoCompleteCountry(searchString).pipe(
            map(x => x.map(country => country.name))
        );
    };

    autoCompleteStateLabelOnly(query: string, locale?: string): Observable<string[]> {
        return this.searchGeographicLocations(query, locale).pipe(map(x => {
            let res = x.map(item => item.province?.name).filter(item => !!item && item.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
            res = [...new Set(res)];
            return res;
        }));
    };

    autoCompleteCityLabelOnly (query: string, locale?: string): Observable<string[]> {
        return this.searchGeographicLocations(query, locale).pipe(map(x => {
            let res = x.map(item => item.city?.name).filter(item => !!item && item.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
            res = [...new Set(res)];
            return res;
        }));
    };

    getCountriesByIsoCode(isoCodes: string[]): Observable<CountryApiModel[]> {
        if (isoCodes.length === 0 ) {
            return of([]);
        }

        if (this.allCountries) {
            return of(
                isoCodes
                    .map(x => this.getCountryByIsoCode(x))
                    .filter(x => !!x.countryCode));
        } else {
            return this.allCountries$.pipe(map(results =>
                results.filter(country => isoCodes.find(x => x === country.countryCode))
            ));
        }
    }

    getAllCountries(): Observable<CountryApiModel[]> {
        return this.allCountries$;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
